import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { message, Modal } from "antd";
import Layout from "./common/Layout";
import Loading from "./common/Loading";
import { get_user } from "../apis/GetUser";
import "../css/userManagement.scss";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";
import { delete_user } from "../apis/DeleteUser";

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false); // 用于防止 React.StrictMode 导致的重复请求

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 从后端获取用户数据
  useEffect(() => {
    // 防止 React.StrictMode 在开发环境下的重复调用
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchUsers();
    }
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await get_user();

      // 根据后端返回格式调整
      if (response && response.data && response.data.code === 200) {
        const userData = response.data.data;

        // 根据后端返回的数据结构，用户列表在 List 字段中
        if (userData && userData.List && Array.isArray(userData.List)) {
          setUsers(userData.List);
        } else {
          console.error("数据格式不正确:", userData);
          setUsers([]);
          message.warning("用户数据格式不正确");
        }
      } else {
        message.error(response.data?.message || "获取用户列表失败");
        setUsers([]);
      }
    } catch (error) {
      console.error("获取用户列表失败：", error);
      message.error("获取用户列表失败：" + (error.message || "网络错误"));
    } finally {
      setLoading(false);
    }
  };

  //搜索用户
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone.includes(searchTerm)
  );

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((user) => user.id));
    }
  };

  const handleAddUser = () => {
    navigate("/add-edit-user");
  };

  const handleEditUser = (user) => {
    navigate("/add-edit-user", { state: { user } });
  };

  //删除用户
  const handleDeleteUser = async (userId) => {
    // 显示删除中的提示
    const hideLoading = message.loading("正在删除用户...", 0);

    try {
      const response = await delete_user(userId);

      // 检查响应状态
      if (response && response.data && response.data.code === 200) {
        hideLoading();
        message.success("删除成功");
        // 重新获取用户列表
        await fetchUsers();
      } else {
        hideLoading();
        message.error(response.data?.message || "删除失败");
      }
    } catch (error) {
      hideLoading();
      console.error("删除用户失败：", error);
      message.error("删除失败：" + (error.message || "网络错误"));
    }
  };

  const getRoleName = (role) => {
    const roleMap = {
      admin: "管理员",
      manager: "经理",
      user: "普通用户",
    };
    return roleMap[role] || role;
  };

  const getStatusName = (status) => {
    return status === "active" ? "禁用" : "启用";
  };

  return (
    <Layout>
      <div className="user-management">
        <div className="page-header">
          <h1>用户管理</h1>
          <div className="header-actions">
            <button className="btn-primary" onClick={handleAddUser}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              添加用户
            </button>
          </div>
        </div>

        <div className="search-bar">
          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="#999" strokeWidth="2" />
              <path
                d="M21 21l-4.35-4.35"
                stroke="#999"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="text"
              placeholder="搜索用户名、邮箱或手机号..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          {selectedUsers.length > 0 && (
            <button
              className="btn-danger"
              onClick={() => {
                Modal.confirm({
                  title: "确认批量删除",
                  content: `确定要删除选中的 ${selectedUsers.length} 个用户吗？`,
                  okText: "删除",
                  cancelText: "取消",
                  okType: "danger",
                  onOk: () => {
                    // TODO: 调用后端批量删除接口
                    setUsers(
                      users.filter((user) => !selectedUsers.includes(user.id))
                    );
                    setSelectedUsers([]);
                    message.success(`成功删除 ${selectedUsers.length} 个用户`);
                    // 可以选择重新获取列表：fetchUsers();
                  },
                });
              }}
            >
              批量删除 ({selectedUsers.length})
            </button>
          )}
        </div>

        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      selectedUsers.length === filteredUsers.length &&
                      filteredUsers.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>ID</th>
                <th>用户名</th>
                <th>邮箱</th>
                <th>手机号</th>
                <th>角色</th>
                <th>状态</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                    />
                  </td>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.phone}</td>
                  <td>
                    <span className={`role-badge role-${user.role}`}>
                      {getRoleName(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${user.status}`}>
                      {getStatusName(user.status)}
                    </span>
                  </td>
                  <td>{user.createTime}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleEditUser(user)}
                        title="编辑"
                      >
                        编辑
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteUser(user.id)}
                        title="删除"
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <Loading size="large" text="正在加载用户数据..." />}

        {!loading && filteredUsers.length === 0 && (
          <div className="empty-state">
            <p>没有找到符合条件的用户</p>
          </div>
        )}
      </div>

      <footer className="page-footer">
        <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </footer>
    </Layout>
  );
};

export default UserManagement;
